import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { trackFailed } from '../../../../../shared-web/track';
import { apiErrorCode, emberApi, type LightAnswer, type PlaceAsk } from '../api';
import { EmberDisc } from '../components/EmberDisc';

/**
 * Ask this light (v4.0 §4.17, 2026-09-21 拍板): the question box on a light's
 * page. A light is a body of lived experience a person can put a question
 * to; the answer is drawn from everyone standing here together and never
 * from any one of them (light-ask.js keeps that structurally — a sentence
 * true of one person never reaches this component).
 *
 * One object on the dusk (2026-09-21 UI 拍板, mock light-page-ui-2026-09-21):
 * Ember's face and the invitation, the field, right under the field the
 * answer, which arrives as paper inside the same card, and under that the
 * questions people asked here as rows — the starting point for someone who
 * does not know what to ask, and, once an answer stands, the next question
 * to ask. The answer took the place right under the field on 2026-10-04
 * (拍板, mock light-ask-answer-place-2026-10-04): from 09-21 it stood under
 * the rows, so what came back landed under a list that had nothing to do
 * with the question just asked, a screen lower on a phone; the rows are a
 * helper for an empty field and have no claim on the place under a filled
 * one. The page carries the light's questions ranked by how many different
 * people asked each (a typed question that the light answered is a row from
 * then on, and rises as others ask it); the rows show three at a time
 * (2026-09-21 拍板: three → five → three; five rows made the ground fill a
 * phone's first screen, so the paper under it was never seen), and where the
 * light has more, `More` at the label's right end turns the next three in,
 * round to the first three again. Each row is at most two lines, so the rows
 * have a ceiling. When a question goes out, the field rises to the top of
 * the view, so the question and the waiting line under it, then the answer,
 * are seen in one frame. Nothing is answered on arrival (四次拍板): a row puts
 * its question into the field and asks it, the same way typed words are
 * asked; while the server answers, a waiting line stands where the answer
 * will. The server keeps each answer with the key of the people it was drawn
 * from, so a light nobody new stood at answers a row from its cache without
 * the model. No history: each answer replaces the last.
 *
 * An answer has three parts (2026-09-22 拍板): what the people here went
 * through, then under its own heading Ember's view — the one thing visible
 * only with everyone here in view — and, when the people who found footing
 * part from the rest on something doable, one suggestion. The view appears
 * only when there is one; the account alone is shown alone. The door to the
 * conversation stood under the suggestion from 2026-09-22 to 09-24; it is
 * the page's composer now, right under this box (PlacePage).
 */
/** Questions offered at once (LIGHT_ASK.CHIPS on the server, which carries up to five turns of them). */
const ROWS = 3;

export function LightAsk({ slug, nodeId, ask }: { slug: string; nodeId: string; ask: PlaceAsk }): ReactElement {
  const [field, setField] = useState('');
  const [current, setCurrent] = useState<{ id: string | null; text: string; answer: LightAnswer['answer'] } | null>(null);
  const [counts, setCounts] = useState(ask.counts);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [from, setFrom] = useState(0);
  const offered = ask.questions.slice(from, from + ROWS);
  const answered = ask.questions.filter((q) => q.answer?.account);
  const turnable = ask.questions.length > ROWS;
  const turn = (): void => setFrom((f) => (f + ROWS >= ask.questions.length ? 0 : f + ROWS));
  const fieldRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    // The field rises to the top of the view as the question goes out; the
    // waiting line stands right under it, and the answer then replaces the
    // line in the same place, without a second move.
    if (busy) fieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [busy]);

  async function put(body: { question_id?: string; text?: string }): Promise<void> {
    setBusy(true);
    setProblem('');
    try {
      const r = await emberApi.askLight(slug, body);
      setCurrent({ id: r.question.id, text: r.question.text, answer: r.answer });
      setCounts(r.counts);
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      const code = apiErrorCode(err);
      // text_rejected (2026-09-23): the question carried something aimed at a
      // person — this page is public.
      // risk_to_self (2026-10-01): the question carried a risk to the person
      // themselves — it is not asked here; the crisis resources answer instead.
      const body = (err as { body?: unknown }).body;
      const resources = code === 'risk_to_self' && body && typeof body === 'object' ? String((body as { resources?: string }).resources || '') : '';
      setProblem(
        status === 429 ? 'That is a lot of questions at once. Give it a few minutes.'
          : code === 'text_rejected' ? 'This page is public, so a question can\u2019t carry anything aimed at a person. Reword it and ask again.'
            : code === 'risk_to_self' ? `This isn\u2019t a question this page can take. ${resources}`.trim()
              : status === 422 ? 'Ask it as a question, in a few words.'
                : 'Ember is briefly unreachable. Try again in a moment.',
      );
      trackFailed('light_ask', status === 429 ? 'too_many' : code === 'text_rejected' || code === 'risk_to_self' ? code : status === 422 ? 'invalid' : 'load_failed');
    } finally {
      setBusy(false);
    }
  }

  const onChip = (q: PlaceAsk['questions'][number]): void => {
    if (busy) return;
    setField(q.text);
    void put({ question_id: q.id });
  };

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    const text = field.trim();
    if (!text || busy) return;
    void put({ text });
  };

  return (
    <div className="place-ask" data-log-seen="place_ask" data-log-node-id={nodeId}>
      {/* Whose box this is (2026-09-21 二次拍板): Ember's — the one who has
          listened to everyone here. Its face and its name, not a new
          character; the answer carries the same face beside the question. */}
      <div className="place-ask-who">
        <EmberDisc face="listening" className="place-ask-disc" />
        <div>
          <h2 className="place-ask-title">Ask what they went through</h2>
          <p className="place-ask-invite">Ember has listened to the people who stood here, and to what came after for many of them. Answers come from many of them together, never from any one.</p>
        </div>
      </div>
      <form ref={fieldRef} className="place-ask-field" onSubmit={onSubmit}>
        <input
          id="place-ask-input"
          className="place-ask-input"
          type="text"
          value={field}
          maxLength={240}
          placeholder="What did people try?"
          aria-label="Ask this light"
          onChange={(e) => setField(e.target.value)}
          disabled={busy}
        />
        <button type="submit" className="place-ask-send" aria-label="Ask" data-log="place_ask_send" data-log-node-id={nodeId} disabled={busy || !field.trim()}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 19V5M5 12l7-7 7 7" /></svg>
        </button>
      </form>
      {problem ? <p className="place-ask-problem" role="alert">{problem}</p> : null}
      {busy ? (
        <p className="place-ask-wait" aria-live="polite">
          <EmberDisc face="thinking" className="place-ask-disc-sm" />
          <span>Drawing on everyone who stood here</span>
          <span className="place-ask-dots" aria-hidden><i /><i /><i /></span>
        </p>
      ) : current ? (
        <div className="place-ask-answer" aria-live="polite">
          <p className="place-ask-q"><EmberDisc face="listening" className="place-ask-disc-sm" /><span>{current.text}</span></p>
          {(current.answer.account ? paragraphsOf(current.answer.account) : ['There isn’t enough here yet on that.']).map((para, i) => (
            <p className="place-ask-text" key={i}>{para}</p>
          ))}
          {current.answer.view ? (
            <div className="place-ask-view">
              <h3 className="place-ask-view-h"><EmberDisc face="thinking" className="place-ask-disc-sm" /><span>Ember’s view</span></h3>
              {paragraphsOf(current.answer.view).map((para, i) => (
                <p className="place-ask-text" key={i}>{para}</p>
              ))}
              {current.answer.suggestion ? <p className="place-ask-suggestion">{current.answer.suggestion}</p> : null}
            </div>
          ) : null}
          <p className="place-ask-from">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="8" r="4" /><path d="M5 21a7 7 0 0 1 14 0" /></svg>
            <span>
              Drawn from <b>{counts.people} people</b>, {counts.embers} {counts.embers === 1 ? 'Ember' : 'Embers'}. Never from any one of them.
              {counts.came_back > 0 ? ` ${counts.came_back} of them came back to tell more.` : ''}
            </span>
          </p>
        </div>
      ) : null}
      {ask.questions.length > 0 ? (
        <div className="place-ask-asked" role="group" aria-label="Questions people here have asked">
          <div className="place-ask-asked-head">
            <p className="place-ask-asked-label">Or ask one people asked here</p>
            {turnable ? (
              <button type="button" className="place-ask-more" data-log="place_ask_more" data-log-node-id={nodeId} onClick={turn} disabled={busy}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 4v5h-5" /></svg>
                <span>More</span>
              </button>
            ) : null}
          </div>
          <ul key={from}>
            {offered.map((q) => (
              <li key={q.id}>
                <button
                  type="button"
                  className="place-ask-row"
                  aria-pressed={current?.id === q.id}
                  data-log="place_ask_chip"
                  data-log-node-id={nodeId}
                  data-log-state={q.id}
                  onClick={() => onChip(q)}
                  disabled={busy}
                >
                  <span className="place-ask-qm" aria-hidden>?</span>
                  <span className="place-ask-row-text">{q.text}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="m9 5 7 7-7 7" /></svg>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {answered.length > 0 ? (
        // Every question with what came back (2026-10-11 拍板), closed until
        // opened: the page reads as before, and the answers are in the
        // document for anyone who opens it and for search engines.
        <details className="place-ask-all">
          <summary>Read what came back to {answered.length === 1 ? 'this question' : `all ${answered.length} questions`}</summary>
          <div className="place-ask-all-body">
            {answered.map((q) => (
              <section key={q.id} className="place-ask-all-item">
                <h3 className="place-ask-all-q">{q.text}</h3>
                {paragraphsOf(q.answer?.account || '').map((para, i) => (
                  <p key={i} className="place-ask-text">{para}</p>
                ))}
                {q.answer?.view ? <p className="place-ask-text place-ask-all-view"><b>Ember’s view.</b> {q.answer.view}</p> : null}
                {q.answer?.suggestion ? <p className="place-ask-text place-ask-all-view">{q.answer.suggestion}</p> : null}
              </section>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

/**
 * The answer in paragraphs. light-ask.js joins the kept sentences with a
 * space; here they are read back in groups of a few sentences, breaking
 * only at a sentence end, so a long answer is read as paper and not as one
 * block.
 */
export function paragraphsOf(text: string, wordsPerParagraph = 80): string[] {
  const sentences = text.trim().split(/(?<=[.!?…]["”’)]?)\s+/).filter(Boolean);
  const out: string[] = [];
  let cur: string[] = [];
  let words = 0;
  for (const s of sentences) {
    const n = s.split(/\s+/).length;
    if (cur.length > 0 && words + n > wordsPerParagraph) { out.push(cur.join(' ')); cur = []; words = 0; }
    cur.push(s);
    words += n;
  }
  if (cur.length > 0) out.push(cur.join(' '));
  return out;
}
